import {
  CAMERA_ROTATION_OPTIONS,
  STREAM_DATA_TYPE_OPTIONS,
  TIME_FILTER_TYPE_OPTIONS,
  TIMESTAMP_SOURCE_OPTIONS,
} from '@vlabs/api-bindings/src/constants';
import {
  EVENT_POLICY_TRIGGER_OPTIONS,
  IMAGE_FORMAT_OPTIONS,
  SENDING_TYPE_OPTIONS,
} from '@vlabs/api-bindings/src/luna-client/constants';

const defaultRoiValues = {
  x: 0,
  y: 0,
  width: 0,
  height: 0,
  mode: 'abs',
};

const defaultValuesMainSettings = {
  name: '',
  description: '',
  group_name: null,
  splittable: 1,
  data: {
    downloadable: true,
    type: STREAM_DATA_TYPE_OPTIONS.videofile,
    reference: '',
    rotation: CAMERA_ROTATION_OPTIONS[0],
    timestamp_source: TIMESTAMP_SOURCE_OPTIONS.auto,
    pts: {
      start_time: 0,
    },
  },
  location: {
    city: null,
    area: null,
    district: null,
    street: null,
    house_number: null,
    geo_position: {
      longitude: null,
      latitude: null,
    },
  },
  autorestart: {
    restart: false,
    attempt_count: 10,
    delay: 60,
  },
};

const defaultValuesPeopleCount = {
  analytic_name: 'people_count',
  parameters: {
    parameters: {
      people_count_gte: 1,
      probe_count: 3,
      event_policy: {
        trigger: EVENT_POLICY_TRIGGER_OPTIONS.start,
        interval: null,
      },
      image_retain_policy: {
        mimetype: IMAGE_FORMAT_OPTIONS.JPEG,
        quality: 1,
        max_size: 640,
      },
      rate: {
        unit: SENDING_TYPE_OPTIONS.frame,
        period: 1,
      },
      roi: { ...defaultRoiValues },
    },
    targets: ['overview'],
    callbacks: [
      {
        type: 'luna-ws-notification',
      },
    ],
  },
};

const defaultValuesWeaponAnalytics = {
  analytic_name: 'weapon_analytics',
  parameters: {
    parameters: {
      weapon_threshold: 0.8,
      min_body_detection_size: 100,
      timeout_interval: 50,
      time_filter: {
        type: TIME_FILTER_TYPE_OPTIONS.median,
        length: 4,
      },
      event_policy: {
        trigger: EVENT_POLICY_TRIGGER_OPTIONS.start,
        interval: null,
      },
      image_retain_policy: {
        mimetype: IMAGE_FORMAT_OPTIONS.JPEG,
        quality: 1,
        max_size: 640,
      },
      rate: {
        unit: SENDING_TYPE_OPTIONS.second,
        period: 1,
      },
      roi: { ...defaultRoiValues },
    },
    targets: ['overview'],
    callbacks: [
      {
        type: 'luna-ws-notification',
        enable: 1,
      },
    ],
  },
};

const defaultValuesFightsAnalytics = {
  analytic_name: 'fights_analytics',
  parameters: {
    parameters: {
      fight_threshold: 0.7,
      min_body_detection_size: 100,
      time_filter: {
        type: TIME_FILTER_TYPE_OPTIONS.median,
        length: 4,
      },
      event_policy: {
        trigger: EVENT_POLICY_TRIGGER_OPTIONS.start,
        interval: null,
      },
      image_retain_policy: {
        mimetype: IMAGE_FORMAT_OPTIONS.JPEG,
        quality: 1,
        max_size: 640,
      },
      rate: {
        unit: SENDING_TYPE_OPTIONS.frame,
        period: 10,
      },
      roi: { ...defaultRoiValues },
    },
    targets: ['overview'],
    callbacks: [
      {
        type: 'luna-ws-notification',
      },
    ],
  },
};

const defaultValuesFireAnalytics = {
  analytic_name: 'fire_analytics',
  parameters: {
    parameters: {
      fire_threshold: 0.7,
      time_filter: {
        type: TIME_FILTER_TYPE_OPTIONS.median,
        length: 4,
      },
      event_policy: {
        trigger: EVENT_POLICY_TRIGGER_OPTIONS.start,
        interval: null,
      },
      image_retain_policy: {
        mimetype: IMAGE_FORMAT_OPTIONS.JPEG,
        quality: 1,
        max_size: 640,
      },
      rate: {
        unit: SENDING_TYPE_OPTIONS.second,
        period: 1,
      },
      roi: { ...defaultRoiValues },
    },
    targets: ['overview'],
    callbacks: [
      {
        type: 'luna-ws-notification',
      },
    ],
  },
};

const defaultValuesFacecoverAnalytics = {
  analytic_name: 'facecover_analytics',
  parameters: {
    parameters: {
      facecover_threshold: 0.45,
      min_body_detection_size: 100,
      timeout_interval: 50,
      time_filter: {
        type: TIME_FILTER_TYPE_OPTIONS.median,
        length: 4,
      },
      event_policy: {
        trigger: EVENT_POLICY_TRIGGER_OPTIONS.start,
        interval: null,
      },
      image_retain_policy: {
        mimetype: IMAGE_FORMAT_OPTIONS.JPEG,
        quality: 1,
        max_size: 640,
      },
      rate: {
        unit: SENDING_TYPE_OPTIONS.second,
        period: 1,
      },
      roi: { ...defaultRoiValues },
    },
    targets: ['facecover'],
    callbacks: [
      {
        type: 'luna-ws-notification',
      },
    ],
  },
};

const defaultValuesBagsAnalytics = {
  analytic_name: 'bags_analytics',
  parameters: {
    parameters: {
      bag_confidence_trh: 0.05,
      rate: {
        unit: SENDING_TYPE_OPTIONS.second,
        period: 5,
      },
      area_trh: 450,
      min_lenght_lost: 70, // опечатка выкачена в апишке, не исправлять на length (04.06.2025)
      event_policy: {
        trigger: EVENT_POLICY_TRIGGER_OPTIONS.start,
        interval: null,
      },
      roi: { ...defaultRoiValues },
      image_retain_policy: {
        mimetype: IMAGE_FORMAT_OPTIONS.JPEG,
        quality: 1,
        max_size: 0,
      },
      iou_static_trh: 0.8,
      human_bbox_expand_coeff: 0.25,
      human_intersection_num_frames_trh: 5,
      human_intersection_new_range_trh: 10,
    },
    targets: ['overview', 'bag'],
    callbacks: [
      {
        type: 'luna-ws-notification',
      },
    ],
  },
};

const defaultValuesLyingAnalytics = {
  analytic_name: 'lying_analytics',
  parameters: {
    parameters: {
      lying_threshold: 0.88,
      min_body_detection_size: 100,
      time_filter: {
        type: TIME_FILTER_TYPE_OPTIONS.median,
        length: 4,
      },
      event_policy: {
        trigger: EVENT_POLICY_TRIGGER_OPTIONS.start,
      },
      image_retain_policy: {
        mimetype: IMAGE_FORMAT_OPTIONS.JPEG,
        quality: 1,
        max_size: 640,
      },
      rate: {
        unit: SENDING_TYPE_OPTIONS.second,
        period: 1,
      },
      roi: { ...defaultRoiValues },
    },
    targets: [],
    callbacks: [
      {
        type: 'luna-ws-notification',
        enable: 1,
      },
    ],
  },
};

const defaultValuesHandsupAnalytics = {
  analytic_name: 'handsup_analytics',
  parameters: {
    parameters: {
      handsup_threshold: 0.88,
      min_body_detection_size: 100,
      time_filter: {
        type: TIME_FILTER_TYPE_OPTIONS.median,
        length: 4,
      },
      event_policy: {
        trigger: EVENT_POLICY_TRIGGER_OPTIONS.start,
      },
      image_retain_policy: {
        mimetype: IMAGE_FORMAT_OPTIONS.JPEG,
        quality: 1,
        max_size: 640,
      },
      rate: {
        unit: SENDING_TYPE_OPTIONS.second,
        period: 1,
      },
      roi: { ...defaultRoiValues },
    },
    targets: [],
    callbacks: [
      {
        type: 'luna-ws-notification',
        enable: 1,
      },
    ],
  },
};

const defaultValuesFallAnalytics = {
  analytic_name: 'fall_analytics',
  parameters: {
    parameters: {
      fall_threshold: 0.5,
      min_body_detection_size: 100,
      time_filter: {
        type: TIME_FILTER_TYPE_OPTIONS.median,
        length: 4,
      },
      event_policy: {
        trigger: EVENT_POLICY_TRIGGER_OPTIONS.start,
      },
      image_retain_policy: {
        mimetype: IMAGE_FORMAT_OPTIONS.JPEG,
        quality: 1,
        max_size: 0,
      },
      rate: {
        unit: SENDING_TYPE_OPTIONS.second,
        period: 1,
      },
      roi: { ...defaultRoiValues },
    },
    targets: ['overview', 'fall'],
    callbacks: [
      {
        type: 'luna-ws-notification',
        enable: 1,
      },
    ],
  },
};

export const defaultValues = {
  roi: defaultRoiValues,
  main: defaultValuesMainSettings,
  crowd: defaultValuesPeopleCount,
  weapon: defaultValuesWeaponAnalytics,
  fight: defaultValuesFightsAnalytics,
  fire: defaultValuesFireAnalytics,
  facecover: defaultValuesFacecoverAnalytics,
  bags: defaultValuesBagsAnalytics,
  lying: defaultValuesLyingAnalytics,
  fall: defaultValuesFallAnalytics,
  handsup: defaultValuesHandsupAnalytics,
};
